import { useState } from 'react';
import axios from 'axios';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import API_BASE_URL from '../../components/config/appConfig';
import { useToast } from '../../components/Toast';

/* Mirrors the server's rules in changePassword, so the button only enables
   for a password the API will accept. */
export const PASSWORD_RULES = [
    { key: 'length', label: 'At least 8 characters', test: (v) => v.length >= 8 },
    { key: 'letter', label: 'Contains a letter', test: (v) => /[A-Za-z]/.test(v) },
    { key: 'number', label: 'Contains a number', test: (v) => /\d/.test(v) },
];

const EMPTY = { currentPassword: '', newPassword: '', confirmPassword: '' };

const FIELDS = [
    { key: 'currentPassword', label: 'Current password', autoComplete: 'current-password' },
    { key: 'newPassword', label: 'New password', autoComplete: 'new-password' },
    { key: 'confirmPassword', label: 'Confirm new password', autoComplete: 'new-password' },
];

const AccountSettings = () => {
    const toast = useToast();
    const [values, setValues] = useState(EMPTY);
    const [visible, setVisible] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const { currentPassword, newPassword, confirmPassword } = values;
    const rulesMet = PASSWORD_RULES.every((rule) => rule.test(newPassword));
    const mismatch = Boolean(confirmPassword) && confirmPassword !== newPassword;
    const sameAsCurrent = Boolean(newPassword) && newPassword === currentPassword;
    const canSubmit = Boolean(currentPassword) && rulesMet && !mismatch && !sameAsCurrent
        && confirmPassword === newPassword && !saving;

    const onChange = (key) => (event) => {
        setValues((prev) => ({ ...prev, [key]: event.target.value }));
        setError('');
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        if (!canSubmit) return;
        setSaving(true);
        try {
            await axios.put(
                `${API_BASE_URL}/users/change-password`,
                { currentPassword, newPassword },
                { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
            );
            setValues(EMPTY);
            setVisible({});
            toast.success('Password updated');
        } catch (err) {
            const message = err?.response?.data?.message || 'Could not update your password. Please try again.';
            setError(message);
            toast.error(message);
        } finally {
            setSaving(false);
        }
    };

    const helperFor = (key) => {
        if (key === 'newPassword' && sameAsCurrent) return 'Must be different from your current password';
        if (key === 'confirmPassword' && mismatch) return 'Passwords do not match';
        return ' ';
    };

    return (
        <section className="pf-card" id="settings" style={{ '--pf-d': '.16s' }}>
            <div className="pf-panel-head">
                <h3 className="pf-panel-title">
                    <TuneRoundedIcon />
                    Account settings
                </h3>
            </div>

            <form className="pf-settings" onSubmit={onSubmit} noValidate>
                <div className="pf-settings-intro">
                    <span className="pf-row-icon"><KeyRoundedIcon /></span>
                    <div>
                        <h4>Change password</h4>
                        <p>Enter your current password, then choose a new one.</p>
                    </div>
                </div>

                <div className="pf-settings-fields">
                    {FIELDS.map(({ key, label, autoComplete }) => (
                        <TextField
                            key={key}
                            className="pf-field"
                            size="small"
                            fullWidth
                            label={label}
                            type={visible[key] ? 'text' : 'password'}
                            autoComplete={autoComplete}
                            value={values[key]}
                            onChange={onChange(key)}
                            error={helperFor(key) !== ' '}
                            helperText={helperFor(key)}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            edge="end"
                                            onClick={() => setVisible((prev) => ({ ...prev, [key]: !prev[key] }))}
                                            aria-label={`${visible[key] ? 'Hide' : 'Show'} ${label.toLowerCase()}`}
                                        >
                                            {visible[key] ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    ))}
                </div>

                <ul className="pf-rules" aria-label="Password requirements">
                    {PASSWORD_RULES.map(({ key, label, test }) => {
                        const met = test(newPassword);
                        return (
                            <li key={key} className={met ? 'is-met' : ''}>
                                {met ? <CheckCircleRoundedIcon /> : <RadioButtonUncheckedRoundedIcon />}
                                {label}
                            </li>
                        );
                    })}
                </ul>

                {error ? <p className="pf-settings-error" role="alert">{error}</p> : null}

                <div className="pf-settings-actions">
                    <button
                        type="button"
                        className="pf-btn is-ghost"
                        onClick={() => { setValues(EMPTY); setError(''); }}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button type="submit" className="pf-btn is-primary" disabled={!canSubmit}>
                        {saving ? 'Updating…' : 'Update password'}
                    </button>
                </div>
            </form>
        </section>
    );
};

export default AccountSettings;
